// src/pages/TrackCreation/Step12.tsx
import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-geometryutil";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import { Map as LeafletMap } from "leaflet";
import { PartnersProps } from "../../../../types/screensTypes";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import { HotInfo } from "../../../../types/types";
import { useGlobalState } from "../../../../context/useGlobalState";
import { getHotInfoTrackId, saveHotInfoTrack, getGeocode } from "../../../../api";
import Dropdown from "../../../../components/common/Dropdown";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import TextInput from "../../../../components/ui/TextInput";
import Button from "../../../../components/ui/Button";
import ImageMarker from "../../../../components/map/MapImageMarker";
import HotInfoList from "../../../../components/lists/HotInfoList";
import { MapInitializer } from "../../../../utils/mapInitializer";
import shell from "../createShell.module.css";
import mapStyles from "../createMap.module.css";
import stepStyles from "../createStep.module.css";

const Partners: React.FC<PartnersProps> = ({ navigateToNext, navigateToContinue }) => {
    const { trackData } = useTrackCreation();
    const [hotInfos, setHotInfos] = useState<HotInfo[]>([]);
    const [map, setMap] = useState<LeafletMap | null>(null);
    const [address] = useState<string>("");
    const [coordinates, setCoordinates] = useState<string>("");
    const [type, setType] = useState<string | null>(null);
    const [cityName, setCityName] = useState<string | null>(null);
    const { data } = useGlobalState();
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    useEffect(() => {
        if (trackData && trackData.trackId) {
            getHotInfo(trackData.trackId);
        }
    }, [trackData.trackId]);

    useEffect(() => {
        if (map && hotInfos.length > 0) {
            const bounds = hotInfos.map((hotInfo) => [hotInfo.point.lat, hotInfo.point.lng]) as [number, number][];
            map.fitBounds(bounds);
        }
    }, [map, hotInfos]);

    const getHotInfo = async (trackId: string): Promise<void> => {
        const token = localStorage.getItem("token");

        if (!token || !trackId) return;

        const response = await getHotInfoTrackId({ token, trackId });
        if (response) {
            setHotInfos(response);
        }
    };

    useEffect(() => {
        const cities = data?.locationCityOptions || [];
        const trackCity = trackData?.city;
        const cityFilter = cities.filter((city) => city.value === trackCity);
        const city = cityFilter.length > 0 ? cityFilter[0] : null;
        if (city) {
            setCityName(city.label);
        }
    }, [data, trackData]);

    const handleMarkerDragEnd = async (newPosition: { lat: number; lng: number }, id: string): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        const { lat, lng } = newPosition;
        if (lat && lng && token && trackId) {
            await saveHotInfoTrack({ token, trackId, id, lat, lng });
            await getHotInfo(trackId);
        }
    };

    const setMapCenterOnReady = (mapInstance: L.Map | null) => {
        if (!mapInstance) {
            return;
        }
        setMap(mapInstance);
    };

    const handleAddPartner = async (): Promise<void> => {
        let latitude: string | undefined;
        let longitude: string | undefined;
        const temporaryId = uuidv4(); // Generate an ID only once
        if (coordinates.trim() !== "") {
            const parts = coordinates.split(",");
            if (parts.length >= 2) {
                latitude = parts[0].trim();
                longitude = parts[1].trim();
            }
        } else if (address.trim() !== "") {
            const geocode = await getGeocode({
                address: address + " " + (cityName || ""),
            });
            if (geocode) {
                latitude = String(geocode.lat);
                longitude = String(geocode.lon);
            } else {
                window.alert(t("addressNotFound"));
            }
        }
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;

        if (!token || !trackId) return;

        if (latitude && longitude && type && temporaryId) {
            await saveHotInfoTrack({
                token,
                trackId,
                id: temporaryId,
                type,
                lat: parseFloat(latitude.trim()),
                lng: parseFloat(longitude.trim()),
            });
            await getHotInfo(trackId);
        }
    };

    const handleMapReady = (mapInstance: L.Map) => {
        setMap(mapInstance);
    };

    const saveHotInfosPosition = async (id: string, position: number): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId) return;
        // Adjust parameters as needed; here we assume the last argument is position.
        await saveHotInfoTrack({
            token,
            trackId,
            id,
            position,
        });
    };

    return (
        <div className={`content-container experience-map ${shell.mapShell} ${mapStyles.partnersShell}`}>
            <div className={mapStyles.leftColumn}>
                <div className={`page-title experience-map-title ${mapStyles.titleBlock}`}>
                    <TitleSubtitle title={t("partners")} subtitle={t("addBusinessPartners")} layout="map" />
                </div>

                <div className={mapStyles.mapContainer}>
                    <div className="map-placeholder">
                        <MapContainer
                            center={[1, 1]}
                            zoom={16}
                            style={{ height: "480px", width: "100%" }}
                            scrollWheelZoom={false}
                            doubleClickZoom={false}
                            whenReady={() => setMapCenterOnReady(map)}
                        >
                            <MapInitializer onMapReady={handleMapReady} />
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            />

                            {hotInfos &&
                                hotInfos.length > 0 &&
                                hotInfos.map((hotinfo) => (
                                    <ImageMarker
                                        key={hotinfo.id}
                                        id={hotinfo.id}
                                        lat={hotinfo.point?.lat}
                                        lng={hotinfo.point?.lng}
                                        type={hotinfo.type ? String(hotinfo.type).toUpperCase() : "OTHER"}
                                        onSelect={() => navigateToNext(hotinfo.id)}
                                        onMarkerDragEnd={handleMarkerDragEnd}
                                    />
                                ))}
                        </MapContainer>
                    </div>
                </div>

                <div className={`experience-map-add ${mapStyles.add}`}>
                    <h3 className={`experience-map-add-title ${mapStyles.addTitle}`}>{t("addANewPartner")}</h3>
                    <div className={`add-waypoint-form experience-map-add-fields ${mapStyles.addFields}`}>
                        <div className="add-waypoint-coordinates">
                            <TextInput
                                placeholder={t("enterCoordinatesLatLng")}
                                value={coordinates}
                                onChange={(e) => setCoordinates(e.target.value)}
                            />
                        </div>
                        <Dropdown
                            label=""
                            options={[
                                { value: "MUSEUM", label: t("monumentsMuseums") },
                                { value: "RESTAURANT", label: t("restaurant") },
                                { value: "TRACK", label: t("track") },
                                { value: "OTHER", label: t("other") },
                            ]}
                            onChange={(value: string) => {
                                setType(value);
                            }}
                            value={type || ""}
                            placeholder={t("selectPartnerType")}
                            loading={false}
                            error={false}
                            isWithoutMargin
                        />
                        <Button
                            label={t("addPartner")}
                            onClick={handleAddPartner}
                            variant="primary"
                            shape="pill"
                            className={`${stepStyles.continueBtn} ${mapStyles.mapBtn}`}
                        />
                    </div>
                </div>
            </div>

            <div className={`right-sidebar experience-map-poi ${mapStyles.poiColumn}`}>
                <div className="waypoints-list">
                    <div className={`waypoints-list-header ${mapStyles.waypointsHeader}`}>
                        <h3>{t("partners")}</h3>
                    </div>
                    <HotInfoList
                        hotinfos={hotInfos}
                        setHotInfos={setHotInfos}
                        saveHotInfos={saveHotInfosPosition}
                        refetchHotInfo={async () => {
                            const trackId = trackData.trackId;
                            if (!trackId) return;
                            await getHotInfo(trackId);
                        }}
                        navigateToNext={navigateToNext}
                    />
                </div>
            </div>

            {navigateToContinue && (
                <div className={`${stepStyles.stepButton} ${stepStyles.footer} ${stepStyles.footerMap}`}>
                    <Button
                        label={t("continue")}
                        onClick={navigateToContinue}
                        variant="primary"
                        shape="pill"
                        className={`${stepStyles.continueBtn} ${mapStyles.mapBtn}`}
                    />
                </div>
            )}
        </div>
    );
};

export default Partners;
